// @flow
import { httpClient } from 'angieslist-app-platform';
import moment from 'moment';
import queryString from 'query-string';
import type { Address } from '../../../app/types';

export type ArchiveShowcaseRequest = {
  showcaseId: number,
  serviceProviderId: string | number,
}

export type GetProfileRequest = {
  serviceProviderId: string,
  zipCode?: string,
};

export type GetProfileRequestUAPI = {
  uuid: string,
}

export type GetProfileCompletionRequests = {
  serviceProviderId: string,
};

type DefaultRequestType = {
  name: { },
  inBusinessSince: {},
  description: {},
  primaryAddress: {},
  primaryContactInfo: {},
  hours: {},
  servicesOffered: {},
  servicesNotOffered: {},
  licenseSignature?: string,
  licenses: {},
  bonded: {},
  insured: {},
  acceptedPayments: {},
  ecoFriendlyAccreditations: {},
  memberOfferings: {},
  seniorDiscount: {},
}

export type BusinessHighlightUAPI = {
  name: string
}

export type AmenitiesUAPI = {
  emergencyServices: boolean,
  freeEstimates: boolean,
  warrantiesOffered: boolean,
  seniorDiscount: boolean,
}

type ProfileOperationString = {
  action: string,
  data: string,
}

type ProfileOperationInteger = {
  action: string,
  data: number
}

type Hours = {
  day: string,
  hoursType: string,
}

type HoursInfo = {
  hours: Hours[],
  acceptsAppointments: boolean
}

type Logo = {
  id: number,
  url: string,
  caption?: string,
  title?: string,
}

type ContactInfo = {
  primaryPhoneNumber?: string,
  secondaryPhoneNumber?: string,
  mobilePhoneNumber?: string,
  faxNumber?: string,
  primaryPhoneNumberFormatted?: string,
  secondaryPhoneNumberFormatted?: string,
  mobilePhoneNumberFormatted?: string,
  firstName?: string,
  middleName?: string,
  lastName?: string,
  email?: string,
  website?: string,
  facebook?: string,
  twitter?: string,
}

type ServiceProviderProperty = {
  propType: string,
  name: string,
  value: any,
}

// omitting awards and categories, as they're zip-specific
export type GetProfileResponse = {
  listingId: string,
  name: string,
  hoursInfo?: HoursInfo,
  joinedAt: Date,
  description?: string,
  servicesOffered?: string,
  serviceProviderType: string,
  status: string,
  inBusinessSince?: number,
  logo?: Logo,
  isCertified: boolean,
  primaryAddress?: Address,
  primaryContactInfo?: ContactInfo,
  properties: ServiceProviderProperty[],
};

export type Completion = {
  percentage: number,
  complete: boolean,
  alert: boolean
};

export type GetProfileCompletionResponse = {
  logo: Completion,
  about: Completion,
  hours: Completion,
  contact: Completion,
  photos: Completion,
  licensing: Completion,
  serviceDetails: Completion,
  showcase: Completion
};

export type ServiceProviderShowcaseListRequest = {
  serviceProviderId: number,
  offset?: number,
  limit?: number,
  statuses: string[],
};

export type ShowcaseMedia = {
  mediaId: number,
  url: string,
  title?: string,
  caption?: string,
  description?: string
};

export type Showcase = {
  showcaseId: number,
  title: string,
  displayMedia?: ShowcaseMedia,
  showcaseStatus: string
};

export type ServiceProviderShowcaseListResponse = {
  showcaseCount: number,
  showcases: Showcase[]
};

export type SaveContactInfoRequest = {
  firstName: string,
  lastName: string,
  primaryPhone: string,
  website?: string,
  email: string,
  addressLine1: string,
  addressLine2?: string,
  postalCode: string,
  stateId: string | number,
  stateAbbreviation: string,
  city: string,
}

export type GetAllBusinessHighlightsResponse = {
  businessHighlights: [{
    businessHighlightTypeId: number,
    name: string,
    description: string,
    iconUrl: string,
  }]
};

export type GetProfileCompletionRequest = {
  serviceProviderId: number,
};

const archiveShowcase = (showcaseId: number, serviceProviderId: string | number): any =>
  httpClient.post(
    `/gateway/office-home/v1/service_provider/${serviceProviderId}/showcase/${showcaseId}/archive`,
    {
      headers: { 'X-ANGI-CallerId': serviceProviderId },
    },
  );

const getServiceProviderCurrentProfile = ({
  serviceProviderId,
  zipCode = '46202', // only used for SSA, not relevant to us at this time; we'll need a new service otherwise
}: GetProfileRequest): any =>
  httpClient.get(`/gateway/service-provider-profile/v5/service_provider/${serviceProviderId}?${queryString.stringify({
    zipCode,
  })}`, {
    headers: { 'X-ANGI-CallerId': serviceProviderId },
  });

/** *** UAPI GET Current Profile **** */
const getServiceProviderProfile = (profileRequestUAPI: GetProfileRequestUAPI) : any =>
  httpClient.get(`/gateway/service-provider-management/resource/proapi/v1/businessprofiles/${profileRequestUAPI.uuid}`, {});


const getUUIDBySpId = (serviceProviderId: string) : any =>
  httpClient.get(`/gateway/service-provider-profile/v1/service_provider/${serviceProviderId}/uuid`, {});

const getProfileCompletionInfo = ({
  serviceProviderId,
}: GetProfileCompletionRequests): any =>
  httpClient.get(`/gateway/service-provider-management/v1/service_provider/${serviceProviderId}/profile/complete`, {
    headers: { 'X-ANGI-CallerId': serviceProviderId },
  });

const getShowcases =
(serviceProviderId: string | number, statuses: string[]): any => {
  const statusQueryParameter = `?statuses=${statuses.join('&statuses=')}`;
  return httpClient.get(`/gateway/office-home/v1/service_provider/${serviceProviderId}/showcases${statusQueryParameter}`, {
    headers: {
      'X-ANGI-CallerType': 'ServiceProvider',
      'X-ANGI-CallerId': serviceProviderId,
    },
  });
};

const getThimbleUrl = (serviceProviderId: number, postalCode: string, categoryId ?: number): any =>
  httpClient.get(`/gateway/office-home/v1/service_provider/liability/quote?${queryString.stringify({
    serviceProviderId,
    postalCode,
    categoryId,
  })}`, {
    headers: {
      'X-ANGI-CallerType': 'ServiceProvider',
      'X-ANGI-CallerId': serviceProviderId,
    },
  });

export const defaultUpdateRequest: DefaultRequestType = {
  name: {
    action: 'Ignore',
  },
  inBusinessSince: {
    action: 'Ignore',
  },
  description: {
    action: 'Ignore',
  },
  primaryAddress: {
    action: 'Ignore',
  },
  primaryContactInfo: {
    action: 'Ignore',
  },
  userInfo: {
    firstName: { action: 'Ignore' },
    lastName: { action: 'Ignore' },
  },
  contactInfo: {
    email: { action: 'Ignore' },
    phoneNumber: { action: 'Ignore' },
    website: { action: 'Ignore' },
    address: {
      street1: { action: 'Ignore' },
      street2: { action: 'Ignore' },
      city: { action: 'Ignore' },
      state: { action: 'Ignore' },
      country: { action: 'Ignore' },
      postalCode: { action: 'Ignore' },
    },
  },
  businessInfo: {
    businessName: { action: 'Ignore' },
    businessDescription: { action: 'Ignore' },
    inBusinessSince: { action: 'Ignore' },
    onAngiSince: { action: 'Ignore' },
    isAdvertiser: { action: 'Ignore' },
    insured: { action: 'Ignore' },
    bonded: { action: 'Ignore' },
    status: { action: 'Ignore' },
    hoursInfo: { action: 'Ignore' },
  },
  servicesOffered: {
    action: 'Ignore',
  },
  servicesNotOffered: {
    action: 'Ignore',
  },
  licenses: {
    action: 'Ignore',
  },
  acceptedPayments: {
    action: 'Ignore',
  },
  ecoFriendlyAccreditations: {
    action: 'Ignore',
  },
  memberOfferings: {
    action: 'Ignore',
  },
  seniorDiscount: {
    action: 'Ignore',
  },
  bonded: {
    action: 'Ignore',
  },
  hours: {
    action: 'Ignore',
  },
  insured: {
    action: 'Ignore',
  },
};

const POString: ProfileOperationString = {
};

const POInteger: ProfileOperationInteger = {
  action: 'update',
  data: 0,
};

const saveProfile = (uuid: string, updateRequest: any): any => httpClient.put(
  `/gateway/service-provider-management/resource/proapi/v1/businessprofiles/${uuid}`,
  {
    body: updateRequest,
  },
);

const saveAbout =
  (
    serviceProviderId: number,
    uuid: string,
    inBusinessSince: number,
    description: string,
    businessHighlights?: BusinessHighlightUAPI[],
  ): any => {
    const updateRequest = {
      businessInfo: {
        businessDescription: Object.assign(POString, {
          action: 'Update',
          data: description,
        }),
        inBusinessSince: Object.assign(POInteger, {
          action: inBusinessSince ? 'Update' : 'Delete',
          data: inBusinessSince,
        }),
        ...(businessHighlights ? { businessHighlights } : {}),
      },
      uuid,
      legacyId: serviceProviderId,
    };
    return saveProfile(uuid, updateRequest);
  };

const saveAboutSegmentation =
  (
    serviceProviderId: number,
    uuid: string,
    description: string,
    businessHighlights?: BusinessHighlightUAPI[],
  ): any => {
    const updateRequest = {
      businessInfo: {
        businessDescription: Object.assign(POString, {
          action: 'Update',
          data: description,
        }),
        ...(businessHighlights ? { businessHighlights } : {}),
      },
      uuid,
      legacyId: serviceProviderId,
    };
    return saveProfile(uuid, updateRequest);
  };

const timeFormats = ['hh:mm a', 'HH:mm'];

const createHoursForDayObj = (day, index, hoursInfo) => {
  if (index === 0) {
    return {};
  }

  if (hoursInfo[`isClosed${day}`]) {
    return { dayOfWeek: index, isClosed: true };
  }

  const zeroTime = moment('00:00', timeFormats);
  const startTime = moment(hoursInfo[`open${day}`], timeFormats);
  const endTime = moment(hoursInfo[`close${day}`], timeFormats);

  if (startTime && endTime) {
    return {
      startTime: moment.duration(startTime.diff(zeroTime)).asMinutes(),
      endTime: moment.duration(endTime.diff(zeroTime)).asMinutes(),
      dayOfWeek: index,
      isClosed: false,
    };
  }
  return {};
};

const saveBusinessHours =
  (serviceProviderId: number, uuid: string, hoursInfo: Object): any => {
    // blank for 0 so index for monday starts at 1
    const days = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const hoursToUpdate = days.map((day, index) => (
      createHoursForDayObj(day, index, hoursInfo)
    )).filter(value => Object.keys(value).length !== 0);

    const updateRequest = Object.assign({}, {
      businessInfo: {
        hoursInfo: {
          action: 'Update',
          data: {
            acceptsAppointments: hoursInfo.byAppointment,
            hoursInfoDetail: hoursToUpdate,
          },
        },
      },
      legacyId: serviceProviderId,
      uuid,
    });
    return saveProfile(uuid, updateRequest);
  };

const saveContactInformation =
(serviceProviderId: string | number, uuid: string, values: SaveContactInfoRequest): any => {
  const {
    firstName, lastName, primaryPhone,
    email, addressLine1, addressLine2, website,
    postalCode, stateAbbreviation, city,
  } = values;

  const updateRequest = Object.assign({}, {
    userInfo: {
      firstName: {
        action: 'Update',
        data: firstName,
      },
      lastName: {
        action: 'Update',
        data: lastName,
      },
    },
    contactInfo: {
      emails: {
        action: 'Update',
        data: [
          {
            email,
            type: 'PRIMARY',
          },
        ],
      },
      phoneNumbers: {
        action: 'Update',
        data: [
          {
            phoneNumber: primaryPhone,
            type: 'PRIMARY',
          },
        ],
      },
      website: {
        action: 'Update',
        data: website || '',
      },
      address: {
        action: 'Update',
        data: {
          street1: addressLine1,
          street2: addressLine2 || '',
          city,
          state: stateAbbreviation,
          postalCode,
        },
      },
    },
    legacyId: serviceProviderId,
    uuid,
  });
  return saveProfile(uuid, updateRequest);
};

const saveServices = (serviceProviderId: string | number, uuid: string, values: Object): any => {
  const validPaymentTypes = {
    financingAvailable: 'Financing Available',
    creditCard: 'CreditCard',
    venmo: 'Venmo',
    zelle: 'Zelle',
    check: 'Check',
    payPal: 'PayPal',
  };
  const acceptedPayments = Object.keys(validPaymentTypes)
    .filter(pT => values[pT] === true)
    .map(pT => validPaymentTypes[pT]);

  const { seniorDiscount, seniorDiscountAmount } = values;

  // member offerings
  const freeEstimatesKey = 'freeEstimates';
  const warrantiesKey = 'warranty';
  const emergencyServiceKey = 'emergencyService';
  // eco friendly
  const ecoFriendlyPropertyKeys = ['LEEDAccredited', 'energyStarPartner', 'EPALeadSafeCertified', 'greenProductWorkPracticeProfessional'];

  const leedAccreditedKey = 'LEEDAccredited';
  const energyStarPartnerKey = 'energyStarPartner';
  const epaLeadSafeCertifiedKey = 'EPALeadSafeCertified';
  const greenProductWorkPracticeProfessionalKey = 'greenProductWorkPracticeProfessional';
  // health and safety
  const covid19VideoCallingKey = 'COVID-19-VideoCalling';

  const updateRequest = {
    servicesOffered: {
      action: 'Update',
      data: values.servicesOffered,
    },
    servicesNotOffered: {
      action: 'Update',
      data: values.servicesNotOffered,
    },
    businessInfo: {
      paymentOptions: {
        action: 'Update',
        data: acceptedPayments,
      },
      amenities: {
        ecoFriendlyAccreditations: ecoFriendlyPropertyKeys
          .filter(key => values[key] === true).length > 0,
        emergencyServices: values[emergencyServiceKey],
        freeEstimates: values[freeEstimatesKey] === 'true' || values[freeEstimatesKey] === true,
        warrantiesOffered: values[warrantiesKey] === 'true' || values[warrantiesKey] === true,
        seniorDiscount: seniorDiscount === 'true' || seniorDiscount === true,
        seniorDiscountValue: seniorDiscountAmount,
      },
    },
    ecoFriendlyAccreditations: {
      leedAccredited: values[leedAccreditedKey],
      energyStarPartner: values[energyStarPartnerKey],
      epaLeadSafeCertified: values[epaLeadSafeCertifiedKey],
      greenProductWorkPracticeProfessional: values[greenProductWorkPracticeProfessionalKey],
    },
    healthAndSafety: {
      videoCalling: values[covid19VideoCallingKey],
    },
    legacyId: serviceProviderId,
    uuid,
  };

  return saveProfile(uuid, updateRequest);
};

const saveStep3 = (
  serviceProviderId: string | number,
  uuid: string,
  amenities: AmenitiesUAPI,
  businessHighlights: BusinessHighlightUAPI[],
): any => {
  // neither of these requires an action: 'Update'
  const updateRequest = {
    businessInfo: {
      amenities,
      businessHighlights,
    },
    legacyId: serviceProviderId,
    uuid,
  };

  return saveProfile(uuid, updateRequest);
};

const saveBondedInsured =
  (serviceProviderId: number, uuid: string, isBonded: boolean, isInsured: boolean): any => {
    const updateRequest = {
      businessInfo: {
        insured: {
          action: 'Update',
          data: isInsured,
        },
        bonded: {
          action: 'Update',
          data: isBonded,
        },
      },
      legacyId: serviceProviderId,
      uuid,
    };

    saveProfile(uuid, updateRequest);
  };

const getAllBusinessHighlights = (): any => httpClient.get('/gateway/service-provider-profile/v1/service_provider/businessHighlights/all');

const getCategoryAutocomplete =
  (serviceProviderId: string | number, query: string, size: number): any =>
    httpClient.get(`/gateway/service-provider/v1/categories?${queryString.stringify({
      query,
      size,
    })}`, {
      headers: {
        'X-ANGI-CallerType': 'ServiceProvider',
        'X-ANGI-CallerId': serviceProviderId,
      },
    });

const createTradeLicenseInfo = (
  serviceProviderId: number,
  uuid: string,
  tradeLicenseInfo: Object,
): any => {
  const createRequest = {
    businessInfo: {
      licenses: {
        action: 'Save',
        data: [
          {
            ...tradeLicenseInfo,
          },
        ],
      },
    },
    legacyId: serviceProviderId,
    uuid,
  };
  return saveProfile(uuid, createRequest);
};

const deleteTradeLicenseInfo = (
  serviceProviderId: number,
  uuid: string,
  tradeLicenseInfoId: number,
): any => {
  const deleteRequest = {
    businessInfo: {
      licenses: {
        action: 'Delete',
        data: [
          {
            licenseId: tradeLicenseInfoId,
          },
        ],
      },
    },
    legacyId: serviceProviderId,
    uuid,
  };
  return saveProfile(uuid, deleteRequest);
};

const updateTradeLicenseInfo = (
  serviceProviderId: number,
  uuid: string,
  tradeLicenseInfo: Object,
): any => {
  const updateRequest = {
    businessInfo: {
      licenses: {
        action: 'Update',
        data: [
          {
            ...tradeLicenseInfo,
          },
        ],
      },
    },
    legacyId: serviceProviderId,
    uuid,
  };
  return saveProfile(uuid, updateRequest);
};

const updateTradeLicenseAttestation = (serviceProviderId: string | number, attestationDate: string):
  any =>
  httpClient.post(`/gateway/service-provider-management/v1/serviceprovider/${serviceProviderId}/license/attestation`, {
    headers: {
      'X-ANGI-CallerType': 'ServiceProvider',
      'X-ANGI-CallerId': serviceProviderId,
    },
    body: { attestationDate },
  });

export default {
  archiveShowcase,
  createTradeLicenseInfo,
  deleteTradeLicenseInfo,
  updateTradeLicenseInfo,
  updateTradeLicenseAttestation,
  getCategoryAutocomplete,
  getServiceProviderCurrentProfile,
  getProfileCompletionInfo,
  getShowcases,
  getThimbleUrl,
  saveAbout,
  saveAboutSegmentation,
  saveBusinessHours,
  saveContactInformation,
  saveServices,
  saveStep3,
  saveBondedInsured,
  getAllBusinessHighlights,
  getServiceProviderProfile,
  getUUIDBySpId,
};



// WEBPACK FOOTER //
// ./src/modules/profile/common/profileService.js